export interface ApplyPointsLimitRequest {
  /**
   * Loyalty account of the contact who would earn points. If the contact
   * doesn't have a loyalty account yet, all `points` totals on the account
   * are `0`.
   */
  account?: LoyaltyAccount;
  /**
   * ID of the contact who would earn loyalty points for the order.
   * @format GUID
   */
  contactId?: string | null;
  /**
   * Number of loyalty points the contact would earn for the order before any
   * limit is applied.
   * @min 1
   * @max 9999999
   */
  pointsToEarn?: number | null;
  /**
   * Total number of loyalty points the contact has earned since the start of
   * the current calendar month, in UTC.
   */
  earnedThisMonth?: number | null;
  /**
   * Total number of loyalty points the contact has earned since the start of
   * the current calendar year, in UTC.
   */
  earnedThisYear?: number | null;
}

/**
 * Subset of a contact's loyalty account information passed to service plugin
 * implementers.
 */
export interface LoyaltyAccount {
  /**
   * Loyalty account ID.
   * @format GUID
   * @readonly
   */
  _id?: string;
  /**
   * Points balance and totals for the loyalty account.
   * @readonly
   */
  points?: Points;
  /**
   * Whether the contact has enough points to redeem at least one reward
   * available on the site.
   * @readonly
   */
  rewardAvailable?: boolean;
  /**
   * Date and time the loyalty account was created.
   * @readonly
   */
  _createdDate?: Date | null;
}

/** Loyalty points balance and totals for a loyalty account. */
export interface Points {
  /**
   * Current points balance available to spend.
   * @readonly
   */
  balance?: number | null;
  /**
   * Total number of points the contact has earned over the lifetime of the
   * account.
   * @readonly
   */
  earned?: number | null;
  /**
   * Total number of points the contact has redeemed over the lifetime of the
   * account.
   * @readonly
   */
  redeemed?: number | null;
  /**
   * Total points adjustments the site owner has applied to the account over
   * its lifetime.
   * @readonly
   */
  adjusted?: number | null;
  /**
   * Total number of points that have expired in the account over its lifetime.
   * @readonly
   */
  expired?: number | null;
}

export interface ApplyPointsLimitResponse {
  /**
   * Number of points to assign for the order, after applying the limit logic.
   * Must be less than or equal to `pointsToEarn`. Higher values are capped at
   * `pointsToEarn`. Set to `0` to block the contact from earning points for
   * the order. Leave unset to assign `pointsToEarn` unchanged.
   * @max 9999999
   */
  adjustedPointsToEarn?: number | null;
}

/** Configuration provided by each installed implementer of the service plugin. */
export interface EarnPointsLimitProviderConfig {
  /**
   * Base URI where the implementer is deployed. Wix sends `ApplyPointsLimit`
   * requests to this URI.
   */
  baseUri?: SpiBaseUri;
}

/** Base URI configuration for a service plugin. Wix uses these URIs to call your service plugin methods. */
export interface SpiBaseUri {
  /**
   * Base URI for your service plugin. Wix appends each method's path to this URI.
   *
   * For example, to receive requests at `https://my-app.com/v1/my-method`, set this field to `https://my-app.com/`.
   * @minLength 6
   * @maxLength 2048
   */
  baseUri?: string;
  /** Alternative URIs for specific methods. Use these to override the default URI for individual methods. */
  alternativeUris?: AlternativeUri[];
}

/** Custom URI for the specified service plugin method. */
export interface AlternativeUri {
  /**
   * Name of the method to call at the absolute URI, in PascalCase. For example, to call [Get Shipping Rates](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/extensions/shipping-rates/shipping-rates-integration-service-plugin/get-shipping-rates) on an alternative URI, specify `GetShippingRates`.
   * @minLength 3
   * @maxLength 128
   */
  methodName?: string;
  /**
   * Absolute URI that Wix calls for this method. Wix doesn't append any path to this URI.
   *
   * The URI must begin with `https://`, such as `https://my-app.com/v1/my-custom-method`.
   * @minLength 6
   * @maxLength 2048
   */
  absoluteUri?: string;
}

/**
 * this message is not directly used by any service,
 * it exists to describe the expected parameters that SHOULD be provided to invoked Velo methods as part of open-platform.
 * e.g. SPIs, event-handlers, etc..
 * NOTE: this context object MUST be provided as the last argument in each Velo method signature.
 *
 * Example:
 * ```typescript
 * export function wixStores_onOrderCanceled({ event, metadata }: OrderCanceledEvent) {
 * ...
 * }
 * ```
 */
export interface Context {
  /** A unique identifier of the request. You may print this ID to your logs to help with future debugging and easier correlation with Wix's logs. */
  requestId?: string | null;
  /**
   * [ISO 4217](https://en.wikipedia.org/wiki/ISO_4217) 3-letter currency code.
   * @format CURRENCY
   */
  currency?: string | null;
  /** An object that describes the identity that triggered this request. */
  identity?: IdentificationData;
  /** A string representing a language and region in the format of `"xx-XX"`. First 2 letters represent the language code according to ISO 639-1. This is followed by a dash "-", and then a by 2 capital letters representing the region according to ISO 3166-2. For example, `"en-US"`. */
  languages?: string[];
  /**
   * The service provider app's instance ID.
   * @format GUID
   */
  instanceId?: string | null;
}

export enum IdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: IdentityType;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}
